/**
 * @ipc/admin-ui · widgets/app-shell · app-shell — island-layout оболочки панели.
 * Body на фоне --background; два «острова»: навбар (слева) + main (справа).
 * Mobile (< md): навбар скрыт, сверху бургер → Sheet с меню.
 * TWA: safe-area insets + mobile header с бургером.
 *
 * Горизонтальный padding контента — единый класс `.ipc-page-pad` (globals.css)
 * и на desktop, и в TWA: PageShell padding не добавляет.
 */
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useSessionQuery } from '@/entities/session';
import { getAccessToken } from '@/shared/api';
import { Skeleton } from '@/shared/ui';
import { initTelegramWebApp, isTwaCompactMode, cn } from '@/shared/lib';
import { NavSidebar } from './nav-sidebar';
import { MobileShellHeader } from './mobile-nav';

function ShellSkeleton({ compact }: { compact: boolean }) {
  if (compact) {
    return (
      <div className="twa-safe-insets flex flex-col">
        <Skeleton className="mx-4 mt-3 h-10 w-auto" />
        <Skeleton className="mx-4 mt-3 h-40 w-auto" />
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col gap-3 overflow-hidden p-3 md:flex-row">
      <Skeleton className="hidden h-[calc(100vh-1.5rem)] w-[244px] rounded-xl md:block" />
      <Skeleton className="h-12 w-full rounded-xl md:hidden" />
      <Skeleton className="h-[calc(100vh-1.5rem)] min-h-[40vh] flex-1 rounded-xl" />
    </div>
  );
}

/** Обёртка Outlet: единый page-pad + min-w-0 (чтобы grid/table не разъезжали layout). */
function PageOutlet() {
  return (
    <div className="ipc-page-pad flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
      <Outlet />
    </div>
  );
}

export function AppShell() {
  const location = useLocation();
  const compact = isTwaCompactMode();
  const hasToken = getAccessToken() !== null;
  const sessionQuery = useSessionQuery();

  useEffect(() => {
    if (compact) {
      initTelegramWebApp();
    }
  }, [compact]);

  // Нет токена — на логин, сохранив целевой путь (TWA deep-link /links?create=1).
  if (!hasToken) {
    const returnTo = `${location.pathname}${location.search}`;
    const qs =
      returnTo !== '/' && returnTo.length > 0
        ? `?returnTo=${encodeURIComponent(returnTo)}`
        : '';
    return <Navigate to={`/login${qs}`} replace />;
  }

  if (sessionQuery.isPending) {
    return <ShellSkeleton compact={compact} />;
  }

  const user = sessionQuery.data;
  if (user) {
    if (compact) {
      // TWA: safe-area + mobile header + тот же page-pad, что desktop.
      return (
        <div className="twa-safe-insets flex flex-col bg-background">
          <MobileShellHeader user={user} className="border-border bg-background" />
          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <PageOutlet />
          </main>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col gap-3 overflow-hidden p-3 md:flex-row">
        {/* Desktop: остров-сайдбар. Mobile: скрыт — открывается бургером. */}
        <div className="hidden shrink-0 md:block">
          <NavSidebar user={user} />
        </div>

        <main
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card',
            'h-[calc(100vh-1.5rem)]',
          )}
        >
          <MobileShellHeader user={user} className="md:hidden" />
          <PageOutlet />
        </main>
      </div>
    );
  }

  return <Navigate to="/login" replace />;
}
